/**
 * FullscreenOdds - Full-screen odds with blue/gold broadcast theme
 * Larger text for mobile/remote viewing
 */

import { TravparkLogo } from './TravparkLogo';

export function FullscreenOdds({
  raceData, sortedHorses, gameType, legNumber, trackName, raceDistance, startMethod, startTime,
  flashingHorses, distChanges, useWebcam, webcamDeviceId, audioDeviceId,
  isGreenOdds, greenOddsLeaving,
  renderDistArrow, isVmix, sommertravetMode
}) {
  return (
    <div 
      data-testid="fullscreen-view"
      style={{ 
        backgroundImage: (useWebcam || isGreenOdds) ? 'none' : `url(https://customer-assets.emergentagent.com/job_37d71e0f-ea96-4348-a27f-5a06f0067365/artifacts/ywps6pb9_file_00000000df9c7243b3636a21f3a725db.png)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: isGreenOdds ? (isVmix ? 'transparent' : (useWebcam ? 'transparent' : '#00FF00')) : (useWebcam ? 'transparent' : '#050F23'),
        height: '100vh', 
        width: '100vw', 
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isGreenOdds ? '30px 80px 80px' : '30px 40px 80px',
        boxSizing: 'border-box',
        position: 'relative'
      }}
    >
      {/* Main panel */}
      <div
        className={isGreenOdds ? (greenOddsLeaving ? 'green-odds-box-exit' : 'green-odds-box-enter') : ''}
        style={{
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          borderRadius: '16px',
          border: '3px solid var(--theme-panel-border)',
          backgroundColor: 'var(--theme-panel-bg)',
          boxShadow: isGreenOdds ? 'none' : `0 0 50px rgba(var(--theme-accent-rgb), 0.2), 0 25px 50px rgba(0,0,0,0.5)`,
          width: isGreenOdds ? '75%' : '100%',
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 1,
          position: 'relative'
        }}
      >

        {/* Header */}
        <div 
          className={isGreenOdds ? (greenOddsLeaving ? 'green-odds-header-exit' : 'green-odds-header-enter') : ''}
          style={{
            
            background: `linear-gradient(135deg, var(--theme-header-gradient) 0%, transparent 100%)`,
            borderBottom: '3px solid var(--theme-panel-border)',
            color: '#FFFFFF', padding: '14px 28px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <TravparkLogo height={80} sommertravetMode={sommertravetMode} />
            <div>
              <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '3.5rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', lineHeight: 1, color: 'var(--theme-accent)' }}>
                {gameType}-{legNumber}
              </div>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: '1.5rem', color: 'rgba(255,255,255,0.6)', marginTop: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {trackName} {raceDistance > 0 ? `• ${raceDistance}m` : ''} {startMethod && `• ${startMethod}`}
                {startTime && (() => {
                  try { return ` • Kl. ${new Date(startTime).toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}`; }
                  catch { return ''; }
                })()}
              </div>
            </div>
          </div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", textAlign: 'right' }}>
            <div style={{ color: 'var(--theme-accent)', fontWeight: 600, fontSize: '1.6rem' }}>DIREKTEODDS</div>
            <div style={{ fontSize: '1.1rem', marginTop: '4px', color: 'rgba(255,255,255,0.5)' }}>Rikstoto.no</div>
          </div>
        </div>

        {/* Table Header */}
        <div 
          className={isGreenOdds ? (greenOddsLeaving ? 'green-odds-header-exit' : 'green-odds-header-enter') : ''}
          style={{
            
            display: 'flex', alignItems: 'center', padding: '8px 24px',
            borderBottom: '2px solid var(--theme-panel-border-subtle)',
            fontFamily: "'Inter', sans-serif", fontSize: '1.05rem', fontWeight: 700,
            color: 'var(--theme-accent)', textTransform: 'uppercase', letterSpacing: '0.03em', flexShrink: 0
          }}
        >
          <div style={{ width: '52px', textAlign: 'center' }}>Nr</div>
          <div style={{ flex: 1, minWidth: '180px' }}>Hest</div>
          <div style={{ width: '85px', textAlign: 'right', paddingRight: '12px' }}>Innsats</div>
          <div style={{ width: '75px', textAlign: 'right', paddingRight: '12px' }}>Vinner</div>
          <div style={{ width: '100px', textAlign: 'right', paddingRight: '12px' }}>Innkjørt</div>
          <div style={{ width: '55px', textAlign: 'center', paddingRight: '8px' }}>Vogn</div>
          <div style={{ width: '50px', textAlign: 'center', paddingRight: '8px' }}>Spor</div>
          <div style={{ width: '80px', textAlign: 'center', paddingRight: '8px' }}>Volte</div>
          <div style={{ width: '80px', textAlign: 'center', paddingRight: '8px' }}>Auto</div>
          <div style={{ width: '65px', textAlign: 'right', paddingRight: '8px' }}>Seier</div>
          <div style={{ width: '70px', textAlign: 'right', paddingRight: '8px' }}>Trippel</div>
          <div style={{ width: '70px', textAlign: 'right' }}>Galopp</div>
        </div>

        {/* Horse List */}
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 1 }}>
          {sortedHorses.slice(0, 10).map((horse, index) => (
            <div
              key={horse.number}
              className={`${flashingHorses.has(horse.number) ? 'flash-update' : ''} ${isGreenOdds ? (greenOddsLeaving ? 'green-odds-row-exit' : 'green-odds-row-enter') : ''}`}
              data-testid={`fullscreen-horse-${horse.number}`}
              style={{
                flex: 1, display: 'flex', alignItems: 'center', padding: '0 24px',
                borderBottom: index < 9 ? '1px solid var(--theme-panel-border-faint)' : 'none',
                backgroundColor: index % 2 === 0 ? 'rgba(255,255,255,0.03)' : 'transparent',
                minHeight: '52px'
              }}
            >
              <div style={{ width: '52px', flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                <div style={{
                  width: '40px', height: '40px', backgroundColor: 'var(--theme-accent)', color: 'var(--theme-accent-dark)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: '1.4rem', borderRadius: '5px'
                }}>
                  {horse.number}
                </div>
              </div>
              <div style={{ flex: 1, minWidth: '180px', overflow: 'hidden', paddingRight: '8px' }}>
                <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: '1.5rem', color: '#FFFFFF', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textTransform: 'uppercase', lineHeight: 1.2 }}>
                  {horse.name}
                </div>
                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: '1.15rem', color: 'rgba(255,255,255,0.5)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.2 }}>
                  {horse.driver}
                </div>
              </div>
              <div style={{ width: '85px', textAlign: 'right', paddingRight: '8px' }}>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '1.4rem', color: 'var(--theme-accent)', fontWeight: 700 }}>
                  {horse.distribution > 0 ? `${horse.distribution.toFixed(1)}%` : '-'}
                </span>
                {renderDistArrow(horse.number)}
              </div>
              <div style={{ width: '75px', textAlign: 'right', paddingRight: '8px' }}>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '1.3rem', color: 'rgba(255,255,255,0.7)' }}>
                  {horse.odds > 0 ? horse.odds.toFixed(1) : '-'}
                </span>
              </div>
              <div style={{ width: '100px', textAlign: 'right', paddingRight: '8px' }}>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '1.2rem', color: 'rgba(255,255,255,0.7)' }}>
                  {horse.total_earnings > 0 ? `${Math.round(horse.total_earnings / 100).toLocaleString('nb-NO')}` : '-'}
                </span>
              </div>
              <div style={{ width: '55px', textAlign: 'center' }}>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '1.2rem', color: 'rgba(255,255,255,0.7)' }}>{horse.sulky || '-'}</span>
              </div>
              <div style={{ width: '50px', textAlign: 'center' }}>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '1.3rem', color: '#FFFFFF', fontWeight: 600 }}>{horse.post_position || horse.number}</span>
              </div>
              <div style={{ width: '80px', textAlign: 'center' }}>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '1.2rem', color: 'rgba(255,255,255,0.7)' }}>{horse.record_volt || '-'}</span>
              </div>
              <div style={{ width: '80px', textAlign: 'center' }}>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '1.2rem', color: 'rgba(255,255,255,0.7)' }}>{horse.record_auto || '-'}</span>
              </div>
              <div style={{ width: '65px', textAlign: 'right', paddingRight: '4px' }}>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '1.2rem', color: 'rgba(255,255,255,0.7)' }}>{horse.win_percentage > 0 ? `${horse.win_percentage}%` : '-'}</span>
              </div>
              <div style={{ width: '70px', textAlign: 'right', paddingRight: '4px' }}>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '1.2rem', color: 'rgba(255,255,255,0.7)' }}>{horse.triple_percentage > 0 ? `${horse.triple_percentage}%` : '-'}</span>
              </div>
              <div style={{ width: '70px', textAlign: 'right' }}>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '1.2rem', color: 'rgba(255,255,255,0.7)' }}>{horse.gallop_percentage > 0 ? `${horse.gallop_percentage}%` : '-'}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div
          className={isGreenOdds ? (greenOddsLeaving ? 'green-odds-footer-exit' : 'green-odds-footer-enter') : ''}
          style={{
            
            padding: '8px 28px', borderTop: '3px solid var(--theme-panel-border)',
            background: `linear-gradient(135deg, var(--theme-header-gradient) 0%, transparent 100%)`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0
          }}
        >
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: '1.2rem', color: 'var(--theme-accent)', fontWeight: 600 }}>
            RIKSTOTO.NO • DIREKTEODDS
          </div>
          <TravparkLogo height={36} sommertravetMode={sommertravetMode} />
        </div>
      </div>
    </div>
  );
}
